/**
 * NodeGraph Configuration
 *
 * Centralized configuration for all NodeGraph rendering, layout, and behavior settings.
 * This file is the single source of truth for graph visualization parameters.
 *
 * Configuration is organized into logical domains:
 * - Rendering: Visual appearance and performance (Sigma.js settings)
 * - Layout: Force-directed positioning algorithms (FA2Layout)
 * - Performance: FPS, debouncing, optimization thresholds
 * - Clustering: Visual grouping and canvas rendering
 * - Positioning: Node placement and spacing parameters
 * - Entities: Default weights and visibility states
 *
 * @module graphConfig
 */

import { NodeBorderProgram } from '@sigma/node-border';
import { NodeImageProgram } from '@sigma/node-image';
import type { Settings as SigmaSettings } from 'sigma/settings';

import {
  AttributeWeight,
  GraphEntityType,
} from '@/components/nodeGraph/utils/types';
import { ResourceType } from '@/types';

// =============================================================================
// Environment Configuration
// =============================================================================

/**
 * Current environment mode
 *
 * Used to enable environment-specific optimizations:
 * - Development: Higher FPS, validation, performance monitoring
 * - UAT: Production-like with some debugging
 * - Production: Optimized for performance and stability
 */
export const GRAPH_ENV = (import.meta.env.MODE || 'development') as
  | 'development'
  | 'production'
  | 'uat';

// =============================================================================
// Rendering Configuration
// =============================================================================

/**
 * Sigma.js Renderer Settings
 *
 * Controls visual rendering, label display, and node/edge appearance.
 * These settings balance visual quality with rendering performance.
 *
 * Current values are optimized for:
 * - Large graphs (1000+ nodes)
 * - Real-time interaction (pan, zoom, select)
 * - Clear relationship visualization (edge labels)
 * - Asset-centric display (image nodes)
 *
 * @see NodeGraph.tsx (lines 68-86) - Original implementation
 */
export const SIGMA_SETTINGS: Partial<SigmaSettings> = {
  // Label rendering
  // Note: Node labels are hidden by default for performance with large graphs
  renderLabels: false,
  renderEdgeLabels: true,
  hideLabelsOnMove: false,
  hideEdgesOnMove: false,
  labelRenderedSizeThreshold: 2,
  labelDensity: 1,
  edgeLabelSize: 15,

  // Node and edge types
  // 'image' nodes display asset icons for visual identification
  // 'arrow' edges show directional relationships between entities
  defaultNodeType: 'image',
  defaultEdgeType: 'arrow',

  // Camera constraints
  // Allows 10x zoom out (0.1) to 5x zoom in (5)
  minCameraRatio: 0.1,
  maxCameraRatio: 5,

  // Technical settings
  // zIndex enables layering of nodes and edges
  // allowInvalidContainer permits initialization before dimensions are known
  zIndex: true,
  allowInvalidContainer: true,

  // Custom node programs
  // Border program: Renders selection borders around nodes
  // Image program: Renders asset icons with proper sizing
  nodeProgramClasses: {
    border: NodeBorderProgram,
    image: NodeImageProgram,
  },
} as const;

/**
 * Sigma container styling
 *
 * CSS styling for the Sigma.js canvas container.
 * Position and z-index are critical for layering with cluster canvas.
 *
 * Z-index hierarchy:
 * - Cluster canvas: 0 (background)
 * - Sigma container: 1 (foreground with nodes/edges)
 *
 * @see NodeGraph.tsx (lines 87-93) - Original implementation
 */
export const SIGMA_CONTAINER_STYLE = {
  width: '100%',
  height: '100%',
  backgroundColor: 'transparent',
  position: 'relative' as const,
  zIndex: 1,
} as const;

// =============================================================================
// Layout Configuration
// =============================================================================

/**
 * Force-Directed Layout Base Parameters
 *
 * These values are dynamically scaled based on graph size using logarithmic
 * scaling to maintain visual clarity across different node counts.
 *
 * Physics analogy: nodes = charged particles, edges = springs
 *
 * Tuning guidelines:
 * - Increase gravity for more compact layouts
 * - Increase scalingRatio for more spread between nodes
 * - Increase initialRadius for wider initial distribution
 * - Increase spreadRange for larger canvas area
 *
 * @see utils/layout.ts (lines 4-27) - Original implementation
 */
export const LAYOUT_BASE_PARAMS = {
  /**
   * Gravity pulls nodes toward the center
   *
   * Lower = more spread, Higher = more compact
   * Scaled down for larger graphs to prevent over-clustering
   *
   * Valid range: 0.01 - 10.0
   * Recommended: 0.05 - 0.2
   */
  gravity: 0.05,

  /**
   * Scaling ratio controls repulsion between nodes
   *
   * Higher = more spread between nodes
   * Scaled up for larger graphs to maintain readable spacing
   *
   * Valid range: 1 - 100
   * Recommended: 30 - 50
   */
  scalingRatio: 80,

  /**
   * Initial radius for random node placement
   *
   * Larger radius = more initial spread before layout algorithm runs
   * Prevents initial node overlap in large graphs
   *
   * Valid range: 50 - 1000
   * Recommended: 200 - 400
   */
  initialRadius: 400,

  /**
   * Initial spread range for random positioning
   *
   * Defines the bounding box (width) for initial layout
   * Larger values work better for wide displays
   *
   * Valid range: 1000 - 10000
   * Recommended: 4000 - 8000
   */
  spreadRange: 6000,
} as const;

/**
 * FA2 (ForceAtlas2) Algorithm Settings
 *
 * Fine-tunes the force-directed layout algorithm behavior.
 * ForceAtlas2 is a physics-based layout designed for network graphs.
 *
 * These settings have been optimized through experimentation for
 * attack surface visualization with diverse node types and relationships.
 *
 * Tuning guidelines:
 * - Enable strongGravityMode for very dense graphs
 * - Enable barnesHutOptimize for graphs with 1000+ nodes (trades accuracy for speed)
 * - Increase slowDown to reduce jitter and oscillation
 * - Adjust edgeWeightInfluence to emphasize/de-emphasize edge weights
 *
 * @see hooks/useGraphLayout.ts (lines 27-38) - Original implementation
 */
export const FA2_ALGORITHM_SETTINGS = {
  /**
   * Strong gravity mode
   *
   * When enabled, pulls all nodes strongly toward center
   * Useful for preventing disconnected components from drifting
   *
   * Current: false (allows nodes to spread naturally)
   */
  strongGravityMode: false,

  /**
   * Barnes-Hut optimization
   *
   * Approximates force calculations for better performance
   * Recommended for graphs with 1000+ nodes
   *
   * Current: false (precise calculations for accuracy)
   */
  barnesHutOptimize: false,

  /**
   * Barnes-Hut theta parameter
   *
   * Controls approximation accuracy (only used if barnesHutOptimize is true)
   * Lower = more accurate but slower
   *
   * Valid range: 0.1 - 1.2
   * Recommended: 0.5 for balance
   */
  barnesHutTheta: 0.1,

  /**
   * Slow down factor
   *
   * Damping to reduce oscillation and jitter
   * Higher values = more stable but slower convergence
   *
   * Valid range: 1.0 - 10.0
   * Recommended: 2.0 - 4.0
   */
  slowDown: 3.0,

  /**
   * Edge weight influence
   *
   * How much edge weights affect node positioning
   * Higher = edges pull nodes together more strongly
   *
   * Valid range: 0 - 2
   * Recommended: 0.5 - 1.5
   */
  edgeWeightInfluence: 1,

  /**
   * Lin-Log mode
   *
   * Uses logarithmic attraction instead of linear
   * Useful for scale-free networks with hub nodes
   *
   * Current: false (linear mode)
   */
  linLogMode: false,

  /**
   * Adjust sizes
   *
   * Whether to prevent node overlap based on node sizes
   * Can slow down layout for large graphs
   *
   * Current: false (allows overlap for performance)
   */
  adjustSizes: false,

  /**
   * Outbound attraction distribution
   *
   * Hub nodes (high degree) have more influence on layout
   * Recommended for scale-free networks
   *
   * Current: true (emphasizes hub nodes)
   */
  outboundAttractionDistribution: true,
} as const;

/**
 * Layout timing calculation
 *
 * Determines how long the layout algorithm runs based on graph size.
 * Larger graphs need more time to settle into stable positions.
 *
 * Formula: min(MAX_TIME, BASE_TIME + nodeCount * PER_NODE_TIME)
 *
 * Examples:
 * - 10 nodes: 3,100ms
 * - 100 nodes: 4,000ms
 * - 500 nodes: 8,000ms
 * - 1000+ nodes: 10,000ms (capped)
 *
 * @param nodeCount - Number of nodes in the graph
 * @returns Settle time in milliseconds
 *
 * @see hooks/useGraphLayout.ts (lines 48-50) - Original implementation
 */
export const calculateLayoutSettleTime = (nodeCount: number): number => {
  const BASE_TIME = 3000;
  const PER_NODE_TIME = 10;
  const MAX_TIME = 10000;

  return Math.min(MAX_TIME, BASE_TIME + nodeCount * PER_NODE_TIME);
};

/**
 * Dynamic layout parameter scaling
 *
 * Adjusts base parameters based on graph size using logarithmic scaling.
 * This maintains visual clarity and spacing across different node counts.
 *
 * Scaling rationale:
 * - Small graphs (< 50 nodes): Need stronger gravity to stay centered
 * - Medium graphs (50-500 nodes): Balanced parameters
 * - Large graphs (500+ nodes): Reduced gravity, increased repulsion for spread
 *
 * @param nodeCount - Number of nodes in the graph
 * @returns Scaled layout parameters
 *
 * @see utils/layout.ts (lines 4-27) - Original implementation
 */
export const computeLayoutParams = (nodeCount: number) => {
  // Logarithmic scaling factor to prevent over-correction for large graphs
  const nodeFactor = Math.log(nodeCount + 1) / Math.log(10);

  return {
    // Gravity decreases as node count increases (minimum 0.01 to prevent drift)
    gravity: Math.max(0.01, LAYOUT_BASE_PARAMS.gravity / nodeFactor / 2),

    // Scaling ratio increases with node count (capped at 30 to prevent excessive spread)
    scalingRatio: Math.min(30, LAYOUT_BASE_PARAMS.scalingRatio * nodeFactor),

    // Initial radius scales with node count for appropriate initial distribution
    initialRadius: LAYOUT_BASE_PARAMS.initialRadius * nodeFactor,

    // Spread range scales with node count for larger canvas area
    spreadRange: LAYOUT_BASE_PARAMS.spreadRange * nodeFactor,

    // Edge weight influence remains constant across graph sizes
    edgeWeightInfluence: FA2_ALGORITHM_SETTINGS.edgeWeightInfluence,
  };
};

// =============================================================================
// Default User Preferences
// =============================================================================

/**
 * Default User Preference Values
 *
 * These are the initial default values for user-configurable settings
 * that are persisted to localStorage and can be changed via UI controls.
 *
 * Note: These are defaults only - actual values come from localStorage
 * if the user has previously customized them.
 */
export const GRAPH_DEFAULTS = {
  /**
   * Default maximum number of nodes to load and display
   *
   * Controls the initial page size for asset queries.
   * Higher values show more data but slower initial load.
   * Users can adjust via "Max Node Representation" input.
   *
   * Valid range: 100 - 5000 nodes
   * Recommended: 1000-2500 for good performance
   * Persisted to: localStorage 'nodeGraph.maxNodes.v1'
   */
  maxNodes: 1000,

  /**
   * Default initial camera zoom ratio
   *
   * Sets initial zoom level when graph first loads
   * undefined = auto-fit (shows all nodes)
   * 0.5 = zoomed out (50%)
   * 1.0 = normal (100%)
   * 1.5 = zoomed in (150%)
   *
   * Current: 1.5 for closer view of nodes
   */
  initialCameraRatio: 0.5,
} as const;

// =============================================================================
// Performance Configuration
// =============================================================================

/**
 * Rendering Performance Settings
 *
 * Balance between visual smoothness and CPU/GPU usage.
 * Environment-aware settings optimize for development vs production.
 *
 * Performance considerations:
 * - Higher FPS = smoother animation but more CPU/GPU load
 * - Lower debounce = faster updates but more frequent redraws
 * - Large graph optimizations trigger at specified threshold
 *
 * @see hooks/useGraphRenderLoop.ts (line 23) - Original FPS implementation
 * @see ClusterCanvas.tsx (line 330) - Original debounce implementation
 */
export const PERFORMANCE_CONFIG = {
  /**
   * Target frames per second for render loop
   *
   * Higher = smoother but more CPU/GPU intensive
   * Environment-aware: 30fps in development for better debugging, 15fps in production
   *
   * Valid range: 10 - 60
   * Recommended: 15 for production, 30 for development
   */
  targetFPS: GRAPH_ENV === 'development' ? 30 : 15,

  /**
   * Debounce delay for cluster canvas redraws
   *
   * Prevents excessive redraws during rapid changes (resize, zoom, etc.)
   * Balance between responsiveness and performance
   *
   * Valid range: 50 - 500ms
   * Recommended: 100ms for good balance
   */
  clusterRedrawDebounce: 100,

  /**
   * Large graph threshold for performance optimizations
   *
   * Graphs above this size trigger performance mode:
   * - Reduce animation complexity
   * - Simplify rendering
   * - Increase debounce times
   *
   * Valid range: 500 - 2000 nodes
   * Recommended: 1000 nodes
   */
  largeGraphThreshold: 1000,

  /**
   * Enable performance monitoring in development
   *
   * Logs frame times, layout duration, and other metrics
   * Only enabled in development to avoid production overhead
   */
  enablePerformanceMonitoring: GRAPH_ENV === 'development',
} as const;

// =============================================================================
// Clustering Configuration
// =============================================================================

/**
 * Cluster Canvas Rendering Settings
 *
 * Controls the visual appearance of cluster hulls (colored boundaries
 * around groups of related nodes).
 *
 * Cluster hulls use convex hull algorithm with padding and smoothing
 * to create visually appealing boundaries that don't tightly wrap nodes.
 *
 * Visual design:
 * - Semi-transparent fills show nodes inside clusters
 * - Smooth curves (Bézier) create organic shapes
 * - Shadow effects add depth perception
 * - Labels show cluster name and node count
 *
 * @see ClusterCanvas.tsx - Complete implementation
 */
export const CLUSTER_CONFIG = {
  /**
   * Padding added around cluster nodes for hull calculation
   *
   * Creates visual spacing between cluster boundary and nodes
   * Prevents hull from tightly wrapping nodes
   *
   * Valid range: 20 - 60 pixels
   * Recommended: 30 pixels
   */
  hullPadding: 30,

  /**
   * Smoothing tension for hull curves
   *
   * Controls how smooth the hull boundary curves are
   * Higher = smoother curves but may overshoot
   *
   * Valid range: 0.0 - 1.0
   * Recommended: 0.2 for subtle smoothing
   */
  hullSmoothTension: 0.2,

  /**
   * Fill opacity for cluster backgrounds
   *
   * Semi-transparent to show underlying nodes
   * Balance between cluster visibility and node clarity
   *
   * Valid range: 0.0 - 1.0
   * Recommended: 0.15 - 0.25
   */
  fillOpacity: 0.18,

  /**
   * Shadow blur for depth effect
   *
   * Adds subtle shadow to cluster boundaries for depth perception
   * Larger values create softer, more diffuse shadows
   *
   * Valid range: 0 - 30 pixels
   * Recommended: 10 - 20 pixels
   */
  shadowBlur: 15,

  /**
   * Border width for cluster hulls
   *
   * Normal: Standard cluster outline
   * Selected: Thicker outline for selected cluster
   */
  borderWidth: {
    normal: 2,
    selected: 3,
  },

  /**
   * Label styling
   *
   * Configuration for cluster name and count labels
   * Displayed at cluster centroid (geometric center)
   */
  label: {
    fontSize: 12,
    padding: 8,
    backgroundOpacity: 0.9,
    borderRadius: 6,
  },

  /**
   * Selected cluster highlight color
   *
   * Crimson highlight matches Chariot brand color
   * Applied to border and fill when cluster is selected
   */
  selectedColor: '#98242D',

  /**
   * Debounce delay for cluster canvas redraws
   *
   * Prevents excessive redraws during rapid changes
   * Same as PERFORMANCE_CONFIG.clusterRedrawDebounce for consistency
   *
   * Valid range: 50 - 500ms
   * Recommended: 100ms
   */
  redrawDebounce: 100,
} as const;

// =============================================================================
// Node Positioning Configuration
// =============================================================================

/**
 * Node positioning parameters for different node types
 *
 * Controls initial placement and spacing for assets, attributes, and other entities.
 * These values define the canvas space and minimum distances between nodes.
 *
 * Positioning strategy:
 * - Asset nodes: Wide random distribution across large canvas
 * - Attribute nodes: Orbit around parent asset (closer)
 * - Other entity nodes: Orbit around parent asset (further out)
 *
 * @see utils/layout.ts (getNodePosition function) - Original implementation
 */
export const NODE_POSITIONING = {
  /**
   * Asset nodes: Main entities in the graph
   *
   * Use wide random distribution across canvas
   * Large canvas prevents initial overlap in big graphs
   */
  asset: {
    width: 4000,
    height: 3000,
    minDistance: 100,
  },

  /**
   * Attribute nodes: Connected to assets
   *
   * Positioned in orbit around parent asset
   * Closer than other entity types for visual hierarchy
   */
  attribute: {
    orbitRadius: 150,
    orbitVariation: 40,
  },

  /**
   * Other entity nodes (risks, technologies, etc.)
   *
   * Positioned further from parent than attributes
   * Creates visual distinction between entity types
   */
  other: {
    orbitRadius: 300,
    orbitVariation: 40,
  },
} as const;

// =============================================================================
// Clustering Behavior Configuration
// =============================================================================

/**
 * Related Attributes for Clustering Modes
 *
 * Defines which additional attributes should remain visible when clustering
 * by a specific attribute. This provides useful context without cluttering
 * the visualization.
 *
 * Key principle: The clustering attribute itself is NOT included here
 * (it shows as cluster hulls, not as individual attribute nodes)
 *
 * Design rationale:
 * - AS clustering: Show geographic (country) and discovery (origin) context
 * - Geographic clustering: Show network (AS) and discovery context
 * - Domain clustering: Clean view with no other attributes (many small clusters)
 * - Origin clustering: Show network and geographic context
 *
 * Usage: When clustering by 'asname', also show attributes: ['country', 'origins', 'asnumber']
 *
 * @example
 * ```typescript
 * const relatedAttrs = CLUSTERING_RELATED_ATTRIBUTES['asname'];
 * // Returns: ['country', 'origins', 'asnumber']
 * ```
 */
export const CLUSTERING_RELATED_ATTRIBUTES: Record<string, readonly string[]> =
  {
    // AS Name clustering: Show other AS info, geography, and discovery source
    asname: ['country', 'origins', 'asnumber', 'asrange'],

    // AS Number clustering: Show AS name, geography, and discovery source
    asnumber: ['country', 'origins', 'asname', 'asrange'],

    // AS Range clustering: Show AS name/number, geography, and discovery source
    asrange: ['country', 'origins', 'asname', 'asnumber'],

    // Country clustering: Show network info and discovery source (NOT country itself)
    country: ['asname', 'asnumber', 'origins'],

    // Origin clustering: Show network and geographic context (NOT origin itself)
    origins: ['asname', 'asnumber', 'country'],
    origin: ['asname', 'asnumber', 'country'], // Alias for origins

    // Domain clustering: Clean view with no other attributes (creates many small clusters)
    domain: [],

    // Public/Private clustering: Show network and geographic context
    private: ['asname', 'country', 'origins'],

    // Attack Surface clustering: Show network and geographic context
    attackSurface: ['asname', 'country', 'origins'],

    // Province/City clustering: Show broader geographic and network context
    province: ['country', 'asname', 'origins'],
    city: ['country', 'province', 'asname', 'origins'],

    // Default fallback: Show nothing (cleanest view for unknown attributes)
    default: [],
  } as const;

/**
 * Entity Visibility During Attribute Clustering
 *
 * Controls whether seeds, risks, and technologies are visible when
 * clustering by attributes (asname, domain, country, etc.).
 *
 * Rationale:
 * - When clustering by attributes, relationship nodes (seeds/risks/tech)
 *   create competing forces that pull same-cluster assets apart
 * - Hiding them produces clean, tight clusters
 * - Users can manually toggle them back on via visibility controls
 *
 * Set to false to always show relationships (noisier but more context)
 */
export const CLUSTERING_AUTO_HIDE_RELATIONSHIPS = true;

// =============================================================================
// Proportional Cluster Sampling Configuration
// =============================================================================

/**
 * Proportional Cluster Sampling Settings
 *
 * Controls how assets are sampled across clusters when maxNodes < totalAssets.
 * Ensures all clusters receive representation proportional to their size.
 *
 * Problem: Loading first N assets sequentially can result in cluster bias
 * (e.g., all 1000 assets from "external" cluster, 0 from "cloud" cluster).
 *
 * Solution: Proportional sampling fetches assets from each cluster based on
 * their relative size, ensuring every cluster is represented.
 *
 * Example:
 * - Total: 10,000 assets in 2 clusters
 * - MaxNodes: 1,000
 * - Cluster "external": 9,500 assets (95%) → 950 displayed
 * - Cluster "cloud": 500 assets (5%) → 50 displayed
 *
 * @see utils/proportionalDistribution.ts - Core algorithm implementation
 * @see hooks/useProportionalAssets.ts - Data fetching implementation
 */
export const PROPORTIONAL_SAMPLING_CONFIG = {
  /**
   * Enable proportional sampling feature
   *
   * When true: Assets fetched proportionally across clusters
   * When false: Assets fetched sequentially (first N assets)
   *
   * Enabled to ensure all clusters are represented in the graph
   */
  enabled: true,

  /**
   * Minimum assets per cluster (guarantee representation)
   *
   * Ensures every non-empty cluster has at least this many assets displayed.
   * Even tiny clusters (0.1% of total) will show minimum representation.
   *
   * Valid range: 1 - 10
   * Recommended: 1 (maximum cluster diversity)
   */
  minAssetsPerCluster: 1,

  /**
   * Maximum assets per cluster (prevent dominance)
   *
   * Caps the number of assets from any single cluster.
   * Useful when one cluster is extremely dominant (>90% of assets).
   *
   * undefined = no cap (proportional allocation only)
   * number = hard cap on cluster representation
   *
   * Valid range: 100 - maxNodes
   * Recommended: undefined (let proportions dictate)
   */
  maxAssetsPerCluster: undefined as number | undefined,

  /**
   * Sampling strategy
   *
   * - 'proportional': Allocate based on (clusterSize / totalSize) * maxNodes
   *
   * Current implementation: 'proportional' only
   */
  strategy: 'proportional' as const,

  /**
   * Batch size for parallel cluster queries
   *
   * When fetching assets for multiple clusters, batch queries to avoid
   * overwhelming the backend.
   *
   * Valid range: 3 - 10
   * Recommended: 5 (balance between speed and backend load)
   */
  clusterQueryBatchSize: 5,

  /**
   * Enable fallback to sequential loading on error
   *
   * If proportional sampling fails (statistics unavailable, query errors):
   * - true: Fall back to useQueryAssets (first N assets)
   * - false: Fail fast with error state
   *
   * Recommended: true (graceful degradation)
   */
  enableFallback: true,

  /**
   * Cache TTL for cluster statistics (milliseconds)
   *
   * How long to cache backend statistics before refetching.
   * Statistics don't change frequently (asset counts are stable).
   *
   * Valid range: 60000 (1 min) - 3600000 (1 hour)
   * Recommended: 600000 (10 minutes)
   */
  statisticsCacheTTL: 600000,

  /**
   * Debug logging for proportional sampling
   *
   * Logs distribution calculations, query counts, timing metrics.
   * Enabled in development, disabled in production.
   */
  enableDebugLogging: GRAPH_ENV === 'development',
} as const;

/**
 * Helper function to check if proportional sampling should be used
 *
 * @param clusterAttribute - Current clustering attribute (e.g., 'asname')
 * @param totalAssets - Total assets in account
 * @param maxNodes - Current maxNodes setting
 * @returns true if proportional sampling should be used
 */
export const shouldUseProportionalSampling = (
  clusterAttribute: string | null,
  totalAssets: number,
  maxNodes: number
): boolean => {
  // Feature flag check
  if (!PROPORTIONAL_SAMPLING_CONFIG.enabled) return false;

  // Must have clustering enabled
  if (!clusterAttribute) return false;

  // Only useful when total > maxNodes (need sampling)
  if (totalAssets <= maxNodes) return false;

  return true;
};

// =============================================================================
// Entity Configuration
// =============================================================================

/**
 * Default Attribute Weights
 *
 * Controls which asset attributes are used for clustering and their importance.
 * Weight of 20 = primary clustering attribute (default), 0 = hidden/inactive
 *
 * Clustering behavior:
 * - Only one attribute can have weight 20 at a time
 * - Weight 20 triggers visual clustering (colored hulls)
 * - Weight 0 attributes are still visible but not used for clustering
 *
 * @see utils/constants.ts (lines 7-16) - Original implementation
 */
export const DEFAULT_ATTRIBUTE_WEIGHTS: AttributeWeight[] = [
  { label: 'AS Name', name: 'asname', weight: 20 },
  { label: 'AS Number', name: 'asnumber', weight: 0 },
  { label: 'AS Range', name: 'asrange', weight: 0 },
  { label: 'Domain', name: 'domain', weight: 0 },
  { label: 'Public/Private', name: 'private', weight: 0 },
  { label: 'Attack Surface', name: 'attackSurface', weight: 0 },
  { label: 'Origin', name: 'origins', weight: 0 },
  { label: 'Country', name: 'country', weight: 0 },
];

/**
 * Default Risk Weights
 *
 * Clustering attributes for vulnerability/risk visualization
 *
 * @see utils/constants.ts (lines 18-21) - Original implementation
 */
export const DEFAULT_RISK_WEIGHTS: AttributeWeight[] = [
  { label: 'Severity', name: 'severity', weight: 0 },
  { label: 'Status', name: 'status', weight: 0 },
];

/**
 * Default Technology Weights
 *
 * Clustering attributes for technology/software visualization
 *
 * @see utils/constants.ts (lines 23-27) - Original implementation
 */
export const DEFAULT_TECHNOLOGY_WEIGHTS: AttributeWeight[] = [
  { label: 'Vendor', name: 'vendor', weight: 0 },
  { label: 'Product', name: 'product', weight: 0 },
  { label: 'Version', name: 'version', weight: 0 },
];

/**
 * Default Seed Weights
 *
 * Clustering attributes for seed/domain visualization
 * Default clustering by domain identifier
 *
 * @see utils/constants.ts (lines 29-40) - Original implementation
 */
export const DEFAULT_SEED_WEIGHTS: AttributeWeight[] = [
  { label: 'Domain', name: 'identifier', weight: 20 },
  { label: 'Name', name: 'name', weight: 0 },
  { label: 'Registrant', name: 'registrant', weight: 0 },
  { label: 'Registrar', name: 'registrar', weight: 0 },
  { label: 'Status', name: 'status', weight: 0 },
  { label: 'Country', name: 'country', weight: 0 },
  { label: 'Email', name: 'email', weight: 0 },
  { label: 'Source', name: 'source', weight: 0 },
  { label: 'Class', name: 'class', weight: 0 },
  { label: 'Origin', name: 'origin', weight: 0 },
];

/**
 * Default SharpHound Weights
 *
 * Clustering attributes for Active Directory object visualization
 * SharpHound is used for AD attack path analysis
 *
 * @see utils/constants.ts (lines 42-46) - Original implementation
 */
export const DEFAULT_SHARPHOUND_WEIGHTS: AttributeWeight[] = [
  { label: 'Object Type', name: 'type', weight: 20 },
  { label: 'Domain', name: 'domain', weight: 15 },
  { label: 'Enabled', name: 'enabled', weight: 10 },
];

/**
 * Entity Weight Configuration Map
 *
 * Maps entity types to their default weight configurations.
 * Used by clustering UI to show appropriate weight controls.
 *
 * @see utils/constants.ts (lines 59-69) - Original implementation
 */
export const ENTITY_WEIGHT_CONFIGS: Record<GraphEntityType, AttributeWeight[]> =
  {
    [ResourceType.Asset]: DEFAULT_ATTRIBUTE_WEIGHTS,
    [ResourceType.Risk]: DEFAULT_RISK_WEIGHTS,
    [ResourceType.Technology]: DEFAULT_TECHNOLOGY_WEIGHTS,
    [ResourceType.Attribute]: DEFAULT_ATTRIBUTE_WEIGHTS,
    [ResourceType.Webpage]: [],
    [ResourceType.Seed]: DEFAULT_SEED_WEIGHTS,
    Tag: [],
    SharpHound: DEFAULT_SHARPHOUND_WEIGHTS,
  };

/**
 * Default Visible Attributes
 *
 * Attributes shown in the graph by default.
 * Users can toggle visibility through the control panel.
 *
 * Attribute categories:
 * - Network: asname, asnumber, asrange
 * - Geographic: country, province, city
 * - Registration: purchased, updated, expiration, registrant, registrar
 * - Security: attackSurface, surface, private
 * - Cloud: cloudService, cloudId, cloudRoot, cloudAccount
 * - Metadata: source, origins, class
 * - Web: attributeName, attributeValue
 *
 * @see utils/constants.ts (lines 121-146) - Original implementation
 */
export const DEFAULT_VISIBLE_ATTRIBUTES = [
  'asname',
  'asnumber',
  'asrange',
  'country',
  'province',
  'city',
  'purchased',
  'updated',
  'expiration',
  'registrant',
  'registrar',
  'attackSurface',
  'cloudService',
  'cloudId',
  'cloudRoot',
  'cloudAccount',
  'source',
  'origins',
  'class',
  'private',
  'attributeName',
  'attributeValue',
] as const;

/**
 * Default Visibility State
 *
 * Initial visibility for entity types and attributes.
 * All entities and attributes are visible by default.
 *
 * State structure:
 * - Entity types: ResourceType values (Risk, Technology, etc.)
 * - Attributes: Attribute names from DEFAULT_VISIBLE_ATTRIBUTES
 *
 * @see utils/constants.ts (lines 162-177) - Original implementation
 */
export const DEFAULT_VISIBILITY_STATE = {
  // Entity types
  [ResourceType.Risk]: true,
  [ResourceType.Technology]: true,
  [ResourceType.Webpage]: true,
  [ResourceType.Seed]: true,
  Tag: true,
  [ResourceType.Attribute]: true,
  SharpHound: true,
  // Attributes (all visible by default)
  ...DEFAULT_VISIBLE_ATTRIBUTES.reduce(
    (acc, attr) => ({ ...acc, [attr]: true }),
    {} as Record<string, boolean>
  ),
} as const;

// =============================================================================
// Validation & Type Safety
// =============================================================================

/**
 * Runtime configuration validation
 *
 * Ensures configuration values are within valid ranges.
 * Throws errors in development, logs warnings in production.
 *
 * Validates:
 * - Performance settings (FPS, debounce)
 * - Layout parameters (gravity, scaling)
 * - Cluster settings (opacity, padding)
 * - Camera constraints (zoom ratios)
 *
 * @throws {Error} In development mode if validation fails
 */
export const validateGraphConfig = () => {
  const errors: string[] = [];

  // Validate performance settings
  if (PERFORMANCE_CONFIG.targetFPS < 1 || PERFORMANCE_CONFIG.targetFPS > 60) {
    errors.push(
      `Invalid targetFPS: ${PERFORMANCE_CONFIG.targetFPS}. Must be between 1-60.`
    );
  }

  if (
    PERFORMANCE_CONFIG.clusterRedrawDebounce < 0 ||
    PERFORMANCE_CONFIG.clusterRedrawDebounce > 1000
  ) {
    errors.push(
      `Invalid clusterRedrawDebounce: ${PERFORMANCE_CONFIG.clusterRedrawDebounce}. Must be between 0-1000ms.`
    );
  }

  // Validate layout parameters
  if (LAYOUT_BASE_PARAMS.gravity < 0 || LAYOUT_BASE_PARAMS.gravity > 10) {
    errors.push(
      `Invalid gravity: ${LAYOUT_BASE_PARAMS.gravity}. Must be between 0-10.`
    );
  }

  if (
    LAYOUT_BASE_PARAMS.scalingRatio < 1 ||
    LAYOUT_BASE_PARAMS.scalingRatio > 100
  ) {
    errors.push(
      `Invalid scalingRatio: ${LAYOUT_BASE_PARAMS.scalingRatio}. Must be between 1-100.`
    );
  }

  // Validate cluster settings
  if (CLUSTER_CONFIG.fillOpacity < 0 || CLUSTER_CONFIG.fillOpacity > 1) {
    errors.push(
      `Invalid fillOpacity: ${CLUSTER_CONFIG.fillOpacity}. Must be between 0-1.`
    );
  }

  if (
    CLUSTER_CONFIG.hullSmoothTension < 0 ||
    CLUSTER_CONFIG.hullSmoothTension > 1
  ) {
    errors.push(
      `Invalid hullSmoothTension: ${CLUSTER_CONFIG.hullSmoothTension}. Must be between 0-1.`
    );
  }

  // Validate camera constraints
  if (
    !SIGMA_SETTINGS.minCameraRatio ||
    SIGMA_SETTINGS.minCameraRatio < 0.01 ||
    SIGMA_SETTINGS.minCameraRatio > 1
  ) {
    errors.push(
      `Invalid minCameraRatio: ${SIGMA_SETTINGS.minCameraRatio}. Must be between 0.01-1.`
    );
  }

  if (
    !SIGMA_SETTINGS.maxCameraRatio ||
    SIGMA_SETTINGS.maxCameraRatio < 1 ||
    SIGMA_SETTINGS.maxCameraRatio > 20
  ) {
    errors.push(
      `Invalid maxCameraRatio: ${SIGMA_SETTINGS.maxCameraRatio}. Must be between 1-20.`
    );
  }

  if (errors.length > 0) {
    const message = `Graph configuration validation failed:\n${errors.join('\n')}`;
    if (GRAPH_ENV === 'development') {
      throw new Error(message);
    } else {
      console.warn(message);
    }
  }
};

// Validate on module load in development
if (GRAPH_ENV === 'development') {
  validateGraphConfig();
}

// =============================================================================
// Configuration Exports
// =============================================================================

/**
 * Complete graph configuration object
 *
 * Export this for comprehensive config access or testing with overrides.
 *
 * @example
 * ```typescript
 * import { GRAPH_CONFIG } from './config/graphConfig';
 *
 * // Access nested config
 * const fps = GRAPH_CONFIG.performance.targetFPS;
 *
 * // Override for testing
 * const testConfig = {
 *   ...GRAPH_CONFIG,
 *   performance: { ...GRAPH_CONFIG.performance, targetFPS: 60 }
 * };
 * ```
 */
export const GRAPH_CONFIG = {
  env: GRAPH_ENV,
  defaults: GRAPH_DEFAULTS,
  sigma: SIGMA_SETTINGS,
  sigmaContainer: SIGMA_CONTAINER_STYLE,
  layout: {
    base: LAYOUT_BASE_PARAMS,
    algorithm: FA2_ALGORITHM_SETTINGS,
    computeParams: computeLayoutParams,
    calculateSettleTime: calculateLayoutSettleTime,
  },
  performance: PERFORMANCE_CONFIG,
  clustering: CLUSTER_CONFIG,
  positioning: NODE_POSITIONING,
  entities: {
    weights: ENTITY_WEIGHT_CONFIGS,
    visibleAttributes: DEFAULT_VISIBLE_ATTRIBUTES,
    defaultVisibility: DEFAULT_VISIBILITY_STATE,
  },
  validate: validateGraphConfig,
} as const;

/**
 * Graph configuration type
 *
 * Use this type for config overrides in tests or runtime modifications.
 *
 * @example
 * ```typescript
 * import type { GraphConfig } from './config/graphConfig';
 *
 * const customConfig: Partial<GraphConfig> = {
 *   performance: { targetFPS: 60 }
 * };
 * ```
 */
export type GraphConfig = typeof GRAPH_CONFIG;
